import React, { useState, useMemo } from 'react';
import axios from 'axios';
import { MapPin, Phone, Mail, Facebook, Instagram, Youtube, Linkedin, Search, Send, Home, Gift, Headphones, PhoneCall, ArrowUpRight, Loader2 } from 'lucide-react';
import { europeBranches as defEU, sriLankaBranches as defSL, otherServices as defOther, company, socials, heroImages } from '../mock/data';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';
import { useSiteSettings } from '../context/SiteSettingsContext';

const iconMap = { facebook: Facebook, instagram: Instagram, youtube: Youtube, linkedin: Linkedin };
const serviceIconMap = { Home, Gift, Headphones, PhoneCall };
const TikTokIcon = (props) => (<svg viewBox="0 0 24 24" fill="currentColor" {...props}><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.77 20.1a6.34 6.34 0 0 0 10.9-4.42V8.71a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.85-.14Z"/></svg>);

const ServiceCard = ({ s }) => {
  const Icon = serviceIconMap[s.icon] || Headphones;
  return (
    <a href={`tel:${s.phone.replace(/\s+/g, '')}`} className="group relative overflow-hidden bg-white border border-neutral-200 hover:border-black transition-all p-7 flex flex-col hover:shadow-xl hover:-translate-y-0.5">
      <div className="absolute top-0 left-0 h-1 w-full bg-gradient-to-r from-[#c8102e] to-black opacity-0 group-hover:opacity-100 transition-opacity" />
      <div className="flex items-start justify-between mb-6">
        <div className="w-14 h-14 rounded-full bg-neutral-50 group-hover:bg-[#c8102e] flex items-center justify-center transition-colors">
          <Icon className="w-6 h-6 text-[#c8102e] group-hover:text-white transition-colors" />
        </div>
        <ArrowUpRight className="w-5 h-5 text-neutral-300 group-hover:text-[#c8102e] group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all" />
      </div>
      <div className="text-[10px] tracking-[0.25em] uppercase text-[#c8102e] font-semibold mb-2">{s.region}</div>
      <h4 className="font-serif-display text-xl font-bold text-black mb-3">{s.name}</h4>
      <p className="text-sm text-neutral-600 leading-relaxed mb-5 flex-1">{s.desc}</p>
      <div className="flex items-center gap-2 text-sm font-semibold text-black group-hover:text-[#c8102e] transition-colors pt-4 border-t border-neutral-100">
        <Phone className="w-3.5 h-3.5" /> {s.phone}
      </div>
    </a>
  );
};

const BranchCard = ({ b }) => (
  <div className={`p-5 border transition-all hover:shadow-lg hover:-translate-y-0.5 group ${b.head ? 'bg-black text-white border-black' : 'bg-white border-neutral-200 hover:border-[#c8102e]'}`}>
    <div className="flex items-start gap-3 mb-3">
      <div className={`w-9 h-9 flex items-center justify-center shrink-0 ${b.head ? 'bg-[#c8102e]' : 'bg-neutral-100 group-hover:bg-[#c8102e]'} transition-colors`}>
        <MapPin className={`w-4 h-4 ${b.head ? 'text-white' : 'text-[#c8102e] group-hover:text-white'} transition-colors`} />
      </div>
      <div className="flex-1">
        <h4 className={`font-serif-display font-bold ${b.head ? 'text-white' : 'text-black'}`}>{b.name}</h4>
        {b.city && <p className={`text-xs ${b.head ? 'text-white/60' : 'text-neutral-500'}`}>{b.city}{b.country ? `, ${b.country}` : ''}</p>}
      </div>
    </div>
    {b.address && <p className={`text-xs mb-3 ${b.head ? 'text-white/70' : 'text-neutral-500'}`}>{b.address}</p>}
    <a href={`tel:${b.phone.replace(/\s+/g, '')}`} className={`flex items-center gap-2 text-sm font-semibold ${b.head ? 'text-[#c8102e]' : 'text-black hover:text-[#c8102e]'} transition-colors`}>
      <Phone className="w-3.5 h-3.5" /> {b.phone}
    </a>
  </div>
);

const Contact = () => {
  const [q, setQ] = useState('');
  const [form, setForm] = useState({ name: '', phone: '', email: '', subject: '', message: '' });
  const [sending, setSending] = useState(false);
  const { toast } = useToast();
  const { getContent, settings } = useSiteSettings();
  const europeBranches = getContent('branches_eu', defEU);
  const sriLankaBranches = getContent('branches_sl', defSL);
  const otherServices = getContent('other_services', defOther);
  const phone = settings?.company_phone || company.phone;
  const phoneRaw = phone.replace(/[^0-9+]/g, '');
  const email = settings?.company_email || company.email;
  const address = settings?.company_address || company.headOfficeAddress;

  const filteredSL = useMemo(() => sriLankaBranches.filter((b) => b.name.toLowerCase().includes(q.toLowerCase())), [q]);
  const filteredEU = useMemo(() => europeBranches.filter((b) => (b.name + (b.city || '') + (b.country || '')).toLowerCase().includes(q.toLowerCase())), [q]);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, form);
      toast({ title: 'Message sent', description: 'Our team will get back to you shortly.' });
      setForm({ name: '', phone: '', email: '', subject: '', message: '' });
    } catch (err) {
      toast({ title: 'Send failed', description: err?.response?.data?.detail || 'Please try again later.' });
    } finally {
      setSending(false);
    }
  };

  return (
    <div>
      <section className="relative h-[340px] flex items-center bg-black">
        <img src={heroImages.road} alt="Contact" className="absolute inset-0 w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/70 to-transparent" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 w-full">
          <div className="flex items-center gap-3 mb-4"><span className="h-px w-10 bg-[#c8102e]" /><span className="eyebrow text-white">Get In Touch</span></div>
          <h1 className="text-white font-serif-display text-5xl md:text-6xl font-bold leading-[1.05]">Contact us anytime</h1>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-14">
            <div className="p-8 border border-neutral-200 hover:border-[#c8102e] transition-colors">
              <div className="w-12 h-12 bg-[#c8102e] flex items-center justify-center mb-5"><MapPin className="w-5 h-5 text-white" /></div>
              <h3 className="font-serif-display text-xl font-bold text-black mb-2">Head Office</h3>
              <p className="text-sm text-neutral-600">{address}</p>
            </div>
            <div className="p-8 border border-neutral-200 hover:border-[#c8102e] transition-colors">
              <div className="w-12 h-12 bg-[#c8102e] flex items-center justify-center mb-5"><Phone className="w-5 h-5 text-white" /></div>
              <h3 className="font-serif-display text-xl font-bold text-black mb-2">Call Us</h3>
              <a href={`tel:${phoneRaw}`} className="text-sm text-neutral-600 hover:text-[#c8102e]">{phone}</a>
            </div>
            <div className="p-8 border border-neutral-200 hover:border-[#c8102e] transition-colors">
              <div className="w-12 h-12 bg-[#c8102e] flex items-center justify-center mb-5"><Mail className="w-5 h-5 text-white" /></div>
              <h3 className="font-serif-display text-xl font-bold text-black mb-2">Email Us</h3>
              <a href={`mailto:${email}`} className="text-sm text-neutral-600 hover:text-[#c8102e] break-all">{email}</a>
            </div>
          </div>

          <div className="mb-10 max-w-xl mx-auto">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search branches by city or name..." className="pl-10 h-12 border-neutral-300 focus-visible:ring-[#c8102e]" />
            </div>
          </div>

          <div className="mb-16">
            <div className="flex items-center gap-3 mb-2"><span className="h-px w-10 bg-[#c8102e]" /><span className="eyebrow">Europe</span></div>
            <h2 className="font-serif-display text-3xl md:text-4xl font-bold text-black mb-2">Europe Branches</h2>
            <p className="text-neutral-600 mb-8">{filteredEU.length} locations across Italy, France, Romania & the UK.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredEU.map((b) => <BranchCard key={b.name} b={b} />)}
            </div>
          </div>

          <div className="mb-16">
            <div className="flex items-center gap-3 mb-2"><span className="h-px w-10 bg-[#c8102e]" /><span className="eyebrow">Sri Lanka</span></div>
            <h2 className="font-serif-display text-3xl md:text-4xl font-bold text-black mb-2">Sri Lanka Branches</h2>
            <p className="text-neutral-600 mb-8">{filteredSL.length} locations serving customers across the island.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {filteredSL.map((b) => <BranchCard key={b.name} b={b} />)}
            </div>
          </div>

          <div className="pt-8 border-t border-neutral-100">
            <div className="flex items-center gap-3 mb-2"><span className="h-px w-10 bg-[#c8102e]" /><span className="eyebrow">Dedicated Support</span></div>
            <h2 className="font-serif-display text-3xl md:text-4xl font-bold text-black mb-2">Other Services</h2>
            <p className="text-neutral-600 mb-8 max-w-2xl">Specialised service lines for pick-ups, gifting and direct customer support — staffed by dedicated teams across Europe and Sri Lanka.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {otherServices.map((s) => <ServiceCard key={s.name} s={s} />)}
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-neutral-50">
        <div className="max-w-6xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-14">
          <div>
            <div className="flex items-center gap-3 mb-4"><span className="h-px w-10 bg-[#c8102e]" /><span className="eyebrow">Send a Message</span></div>
            <h2 className="font-serif-display text-4xl md:text-5xl font-bold text-black mb-5 leading-[1.05]">We’ll be in touch shortly</h2>
            <p className="text-neutral-600 mb-8 leading-relaxed">Fill in the form and one of our experts will get back to you within one business day — always.</p>
            <div className="space-y-3 mb-8">
              <a href={`tel:${phoneRaw}`} className="flex items-center gap-3 text-neutral-700 hover:text-[#c8102e]"><Phone className="w-4 h-4 text-[#c8102e]" /> {phone}</a>
              <a href={`mailto:${email}`} className="flex items-center gap-3 text-neutral-700 hover:text-[#c8102e]"><Mail className="w-4 h-4 text-[#c8102e]" /> {email}</a>
            </div>
            <div className="flex items-center gap-2">
              {socials.map((s) => {
                const Icon = s.icon === 'tiktok' ? TikTokIcon : iconMap[s.icon];
                if (!Icon) return null;
                return (
                  <a key={s.name} href={s.url} target="_blank" rel="noreferrer" className="w-10 h-10 border border-neutral-200 hover:bg-[#c8102e] hover:border-[#c8102e] hover:text-white flex items-center justify-center transition-colors text-black">
                    <Icon className="w-4 h-4" />
                  </a>
                );
              })}
            </div>
          </div>
          <form onSubmit={submit} className="bg-white p-8 border border-neutral-200 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input required placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-12 border-neutral-300 focus-visible:ring-[#c8102e]" />
              <Input required placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="h-12 border-neutral-300 focus-visible:ring-[#c8102e]" />
            </div>
            <Input required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-12 border-neutral-300 focus-visible:ring-[#c8102e]" />
            <Input required placeholder="Subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="h-12 border-neutral-300 focus-visible:ring-[#c8102e]" />
            <Textarea required placeholder="Your message" rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="border-neutral-300 focus-visible:ring-[#c8102e]" />
            <Button type="submit" disabled={sending} className="bg-[#c8102e] hover:bg-black text-white h-12 w-full font-semibold tracking-[0.15em] uppercase text-[13px]">{sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />} Send Message</Button>
          </form>
        </div>
      </section>
    </div>
  );
};

export default Contact;
